import { Controller } from "@hotwired/stimulus"

// Page-level infinite scroll for the All Phases Kanban board.
// Uses window scroll events to detect when the user nears the bottom of the
// tallest column, then fetches the next page for all columns that still have
// data and appends the items.
export default class extends Controller {
  static targets = ["column", "sentinel"]
  static values  = { view: String, cardStyle: String, grouping: String, layout: String }

  connect() {
    this.page = 1
    this.loading = false
    this.columnDone = {}
    this.abortController = null
    this._framesSettled = false

    // Primary trigger: scroll (leading + trailing edge throttle)
    this._onScroll = this._throttle(() => this._checkAndLoad(), 200)
    window.addEventListener("scroll", this._onScroll, { passive: true })

    // Safety net: scrollend fires once when scrolling stops — catches fast
    // scroll-and-release that the throttle's trailing edge might still miss.
    this._onScrollEnd = () => this._checkAndLoad()
    window.addEventListener("scrollend", this._onScrollEnd, { passive: true })

    // Detect when turbo-frames finish loading (initial + search reloads).
    // Debounce so we wait until ALL frames have settled.
    this._onFrameLoad = (e) => {
      if (!e.target.dataset?.phase) return
      this._onFramesChanged()
    }
    this.element.addEventListener("turbo:frame-load", this._onFrameLoad)

    // Safety net: if turbo:frame-load never fires, poll the DOM once
    this._initialCheck = setTimeout(() => {
      if (!this._framesSettled) {
        this._framesSettled = true
        this._checkAndLoad()
      }
    }, 2000)
  }

  disconnect() {
    clearTimeout(this._initialCheck)
    clearTimeout(this._frameDebounce)
    clearTimeout(this._recheckTimer)
    // Fire one last trailing-edge call to clean up, then remove listeners
    window.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("scrollend", this._onScrollEnd)
    this.abortController?.abort()
    this.element.removeEventListener("turbo:frame-load", this._onFrameLoad)
  }

  // ── Frame load handling ─────────────────────────────────────────

  _onFramesChanged() {
    // Abort any in-flight pagination (it's for stale data)
    this.abortController?.abort()
    this.abortController = null
    this.loading = false
    this.page = 1
    this.columnDone = {}
    this._framesSettled = false

    // Debounce: wait for all frames to finish loading
    clearTimeout(this._frameDebounce)
    this._frameDebounce = setTimeout(() => {
      this._framesSettled = true
      this._checkAndLoad()
    }, 300)
  }

  // ── Core loading logic ──────────────────────────────────────────

  _checkAndLoad(force = false) {
    if (this.loading || !this._framesSettled) return

    const phases = this._activePhases()
    if (phases.length === 0) {
      this._hideSentinel()
      return
    }

    if (!force && !this._isNearBottom() && !this._isAtDocBottom()) {
      this._hideSentinel()
      return
    }

    this._loadNextPage(phases)
  }

  async _loadNextPage(phases) {
    this.loading = true
    this._showSentinel()
    const nextPage = this.page + 1

    // Capture scroll state BEFORE the fetch — after items append the page
    // grows and both _isNearBottom / _isAtDocBottom will return false even
    // though the user is still waiting for more data.
    const wasAtDocBottom = this._isAtDocBottom() || this._isNearBottom()

    this.abortController = new AbortController()
    const { signal } = this.abortController

    try {
      const results = await Promise.all(
        phases.map(p => this._fetchPage(p, nextPage, signal))
      )
      if (signal.aborted) return

      results.forEach(({ phase, items, isLastPage }) => {
        const container = document.getElementById(`phase-${phase}-items`)
        if (container) {
          items.forEach(el => container.appendChild(document.adoptNode(el)))
        }
        if (isLastPage) this.columnDone[phase] = true
      })

      this.page = nextPage
    } catch (e) {
      if (e.name === "AbortError") return
      console.error("Infinite scroll error:", e)
    } finally {
      if (!signal.aborted) {
        this.loading = false
        this.abortController = null
        this._hideSentinel()
        // If the user was at/near the bottom when we started loading,
        // force one more check — the page grew from appended items so
        // the normal position checks would fail, but we know the user
        // wants more data.
        this._checkAndLoad(wasAtDocBottom)
        clearTimeout(this._recheckTimer)
        this._recheckTimer = setTimeout(() => this._checkAndLoad(), 300)
      }
    }
  }

  async _fetchPage(phase, page, signal) {
    const url = this._buildUrl(phase, page)
    const resp = await fetch(url, { headers: { Accept: "text/html" }, signal })
    const html = await resp.text()

    const doc = new DOMParser().parseFromString(html, "text/html")
    const itemsDiv = doc.getElementById(`phase-${phase}-items`)
    const items = itemsDiv ? [...itemsDiv.children] : []
    const isLastPage = itemsDiv?.dataset.lastPage === "true" || items.length === 0

    return { phase, items, isLastPage }
  }

  // ── Helpers ─────────────────────────────────────────────────────

  _activePhases() {
    return this.columnTargets
      .map(c => c.dataset.phase)
      .filter(phase => {
        if (this.columnDone[phase]) return false
        const items = document.getElementById(`phase-${phase}-items`)
        if (!items) return false // frame hasn't loaded yet
        if (items.dataset.lastPage === "true") {
          this.columnDone[phase] = true
          return false
        }
        return true
      })
  }

  _isNearBottom() {
    let maxBottom = 0
    this.columnTargets.forEach(col => {
      maxBottom = Math.max(maxBottom, col.getBoundingClientRect().bottom)
    })
    return maxBottom > 0 && maxBottom < window.innerHeight + 300
  }

  _isAtDocBottom() {
    const scrollBottom = window.innerHeight + window.scrollY
    const docHeight = document.documentElement.scrollHeight
    return docHeight - scrollBottom < 200
  }

  _showSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.remove("hidden")
  }

  _hideSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
  }

  _buildUrl(phase, page) {
    const url = new URL(
      `/all_phases/all_phases_projects`,
      window.location.origin
    )
    url.searchParams.set("phase", phase)
    url.searchParams.set("view", this.viewValue)
    url.searchParams.set("page", page)

    const input = this.element.querySelector("[data-all-phases-search-target='input']")
    const search = input?.value?.trim()
    if (search) url.searchParams.set("search", search)

    // Include sort
    const sortSelect = this.element.querySelector("[data-all-phases-search-target='sort']")
    const sort = sortSelect?.value
    if (sort) url.searchParams.set("sort", sort)

    // Include the card layout, so page 2+ matches page 1 instead of reverting
    // to the compact default. Read live from the dataset — the search
    // controller rewrites it when the user flips the toggle.
    const cardStyle = this.element.dataset.infiniteScrollCardStyleValue || this.cardStyleValue
    if (cardStyle) url.searchParams.set("card_style", cardStyle)
    const grouping = this.element.dataset.infiniteScrollGroupingValue || this.groupingValue
    if (grouping) url.searchParams.set("grouping", grouping)
    if (this.layoutValue) url.searchParams.set("layout", this.layoutValue)

    // Include region filter
    const checkboxes = this.element.querySelectorAll("[data-all-phases-search-target='regionCheckbox']")
    if (checkboxes.length > 0) {
      const checked = [...checkboxes].filter(cb => cb.checked)
      if (checked.length < checkboxes.length) {
        checked.forEach(cb => url.searchParams.append("regions[]", cb.value))
      }
    }

    return url.toString()
  }

  _throttle(fn, ms) {
    let last = 0
    let trailing = null
    return () => {
      const now = Date.now()
      clearTimeout(trailing)
      if (now - last >= ms) {
        last = now
        fn()
      } else {
        // Trailing edge: guarantees the last call fires even if the user
        // stops scrolling between throttle windows.
        trailing = setTimeout(() => {
          last = Date.now()
          fn()
        }, ms - (now - last))
      }
    }
  }
}
